// Small "added to cart" toast with product image and title, shown after a quick add instead of the cart drawer.
class SCartToast extends HTMLElement {
  #ac;
  #timer = 0;
  #remaining = 0;
  #startedAt = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    document.addEventListener('s-cart-toast:show', (event) => this.#show(event.detail || {}), opts);
    // Back button from the cart page (merchant report Oct 31): the browser restores this page from its back/forward
    // cache with the toast still open and its timer frozen. Close it instantly whenever the page is left or restored.
    const closeNow = () => {
      clearTimeout(this.#timer);
      this.removeAttribute('data-open');
      this.hidden = true;
    };
    window.addEventListener('pagehide', closeNow, opts);
    window.addEventListener('pageshow', (event) => { if (event.persisted) closeNow(); }, opts);
    this.querySelector('[data-toast-close]')?.addEventListener('click', () => this.#hide(), opts);
    this.addEventListener('mouseenter', () => this.#pause(), opts);
    this.addEventListener('mouseleave', () => this.#resume(), opts);
    this.addEventListener('focusin', () => this.#pause(), opts);
    this.addEventListener('focusout', () => this.#resume(), opts);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('data-open')) this.#hide();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  async #show({ title, image }) {
    // A pending hide from an earlier toast must not switch this one off mid-entry.
    clearTimeout(this.#timer);
    const wasOpen = this.hasAttribute('data-open');
    const titleEl = this.querySelector('[data-toast-title]');
    const imageEl = this.querySelector('[data-toast-image]');
    if (imageEl instanceof HTMLImageElement) {
      if (image && imageEl.getAttribute('src') !== image) {
        // Anti-flicker (merchant report Oct 31): decode the product image before the toast appears, so it does not
        // pop in (or show the previous product) while the toast is already fading in. Capped so a slow image never
        // delays the confirmation by more than a moment.
        const loader = new Image();
        loader.src = image;
        await Promise.race([
          loader.decode().catch(() => {}),
          new Promise((resolve) => setTimeout(resolve, 450)),
        ]);
        imageEl.src = image;
      }
      imageEl.hidden = !image;
    }
    if (titleEl) titleEl.textContent = title || '';
    this.#remaining = Number(this.dataset.duration) || 4000;
    if (!wasOpen) {
      this.hidden = false;
      // Commit the hidden-state styles before opening, so the fade/slide always starts from the closed pose
      // (a single rAF is not enough on iOS Safari and made the toast flash at full opacity first).
      void this.offsetHeight;
      requestAnimationFrame(() => requestAnimationFrame(() => this.setAttribute('data-open', '')));
    }
    this.#startedAt = Date.now();
    this.#timer = setTimeout(() => this.#hide(), this.#remaining);
  }

  #hide() {
    clearTimeout(this.#timer);
    this.removeAttribute('data-open');
    this.#timer = setTimeout(() => { this.hidden = true; }, 300);
  }

  #pause() {
    if (!this.hasAttribute('data-open')) return;
    clearTimeout(this.#timer);
    this.#remaining -= Date.now() - this.#startedAt;
  }

  #resume() {
    if (this.hidden) return;
    clearTimeout(this.#timer);
    this.#startedAt = Date.now();
    this.#timer = setTimeout(() => this.#hide(), Math.max(this.#remaining, 1200));
  }
}

if (!customElements.get('s-cart-toast')) customElements.define('s-cart-toast', SCartToast);
